import React from 'react'

import {
  STATUS_RING_ORDER,
  getStatusTheme,
  statusLabel,
} from '../../../modes/attendance/attendanceStatusTheme'
import {AttendanceStatusSegment} from '../../../modes/attendance/buildAttendanceVisualSummary'

type AttendanceSummaryRingProps = {
  segments: AttendanceStatusSegment[]
  presentPercent: number | null
  totalRecords: number
  theme: 'light' | 'dark'
  /** What `totalRecords` counts, when the backend named it (e.g. `days absent`). */
  headlineLabel?: string | null
}

const SIZE = 72
const STROKE = 8
const RADIUS = (SIZE - STROKE) / 2
/** Arcs are measured against a normalized path rather than 2πr, so a segment is literally its
 *  percentage of the ring. The float drift in `count / total * 2πr` accumulated across segments and
 *  left the last arc ending a fraction of a pixel short of the first — a nick in the ring at twelve
 *  o'clock that read as a misdrawn chart. */
const PATH_LENGTH = 100
/** Butt-capped arcs that merely touch antialias against each other, so the darker track shows
 *  through as a hairline at every colour boundary. Each arc overruns its successor by this much and
 *  the successor, drawn after it, paints over the overrun — leaving a solid join. The last arc gets
 *  no overrun, which would bleed it over the first one at twelve o'clock. */
const JOIN_OVERLAP = 0.6
/** The SVG circle starts at three o'clock; attendance rings read from the top. */
const START_AT_TWELVE = `rotate(-90 ${SIZE / 2} ${SIZE / 2})`

const AttendanceSummaryRing: React.FC<AttendanceSummaryRingProps> = ({
  segments,
  presentPercent,
  totalRecords,
  theme,
  headlineLabel,
}) => {
  const muted = theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d'
  const track = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'

  const ordered = STATUS_RING_ORDER.flatMap((key) => {
    const seg = segments.find((s) => s.key === key)
    return seg ? [seg] : []
  }).concat(segments.filter((s) => !STATUS_RING_ORDER.includes(s.key)))

  // The circle is the rate's denominator, so only the statuses inside the rate are drawn on it.
  // Charting the rest as well made the arc and the number in the centre describe different totals:
  // a day with one unmarked student drew the attended arc at 103/120 (85.8%) under a label reading
  // 86.6%, the true 103/119. Unmarked and Checkout are still reported — they get their own bar
  // beside the ring — but they are not marks, and the rate was never taken over them.
  const charted = ordered.filter((s) => s.countsTowardRate !== false)
  // Nothing but out-of-rate statuses (an all-unmarked result) still deserves a chart; there is no
  // rate to contradict in that case, so draw what there is.
  const arcSegments = charted.length > 0 ? charted : ordered

  const total = arcSegments.reduce((sum, s) => sum + s.count, 0) || totalRecords || 1
  // One status fills the whole ring, and a dash pattern with a zero-length gap is the one case
  // renderers disagree on. Draw it as the plain circle it is.
  const isFullRing = arcSegments.length === 1
  let accumulated = 0

  const arcs = arcSegments.map((seg, index) => {
    const length = (seg.count / total) * PATH_LENGTH
    const next = arcSegments[index + 1]
    // Never overrun further than the next arc is long, or a one-record sliver would be buried by the
    // segment before it instead of painted over it.
    const overrun = next ? Math.min(JOIN_OVERLAP, (next.count / total) * PATH_LENGTH) : 0
    const drawn = length + overrun
    const dashoffset = -accumulated
    accumulated += length
    const color = getStatusTheme(seg.label, theme).color
    return (
      <circle
        key={seg.key}
        cx={SIZE / 2}
        cy={SIZE / 2}
        r={RADIUS}
        fill='none'
        stroke={color}
        strokeWidth={STROKE}
        pathLength={PATH_LENGTH}
        strokeDasharray={isFullRing ? undefined : `${drawn} ${PATH_LENGTH - drawn}`}
        strokeDashoffset={isFullRing ? undefined : dashoffset}
        strokeLinecap='butt'
      />
    )
  })

  const hasPresent = segments.some((s) => s.key === 'present' || s.key === 'excused')
  // A result filtered to a single status draws one full-circle arc, so any rate off it is trivially
  // 100% or 0%: a list of the 96 students present read "100% attendance", which is not the school's
  // attendance rate and not something the query measured. Chart the count and name the status.
  const soleSegment = segments.length === 1 ? segments[0] : null
  // A named headline is the figure the question asked for, so it outranks the present rate — the
  // ring may now chart every status, and without this an "absences" answer would centre on the
  // present percentage instead of the absence count.
  const showPercent = !headlineLabel && !soleSegment && presentPercent !== null && hasPresent

  let centerValue: string
  // A bare "records" makes the ring read as the size of the range searched.
  //
  // "attendance", not "present": the figure is the backend's attendance rate, which credits Late as
  // attended and divides by the marks actually recorded (101/117 today). Present alone would be a
  // different number (96/117), so labelling this one "present" states a figure the data never gave.
  let centerLabel: string
  if (showPercent) {
    centerValue = `${presentPercent}%`
    centerLabel = 'attendance'
  } else if (headlineLabel) {
    centerValue = String(totalRecords)
    centerLabel = headlineLabel
  } else if (soleSegment) {
    centerValue = String(soleSegment.count)
    centerLabel = statusLabel(soleSegment.label)
  } else {
    centerValue = String(totalRecords)
    centerLabel = 'records'
  }

  return (
    <div
      className='attendance-summary-ring flex-shrink-0 position-relative'
      style={{width: SIZE, height: SIZE}}
      aria-hidden
    >
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill='none'
          stroke={track}
          strokeWidth={STROKE}
        />
        <g transform={START_AT_TWELVE}>{arcs}</g>
      </svg>
      <div
        className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-center'
        style={{lineHeight: 1.1, maxWidth: SIZE, padding: `0 ${STROKE}px`}}
      >
        <div
          style={{
            fontSize: showPercent ? '0.95rem' : '1.1rem',
            fontWeight: 700,
            color: theme === 'dark' ? '#fff' : '#212529',
          }}
        >
          {centerValue}
        </div>
        <div style={{fontSize: '0.6rem', color: muted, textTransform: 'lowercase'}}>
          {centerLabel}
        </div>
      </div>
    </div>
  )
}

export default AttendanceSummaryRing
